import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebase';
import { DailyProduction, ProductionSummaryRow, ProductionWarehouseData } from '../types/inventory';
import { UnifiedInventoryTransactionService } from './unifiedInventoryTransactions';

export class ProductionService {
  
  /**
   * Get or create daily production record
   */
  static async getDailyProduction(
    date: string, 
    warehouseId: string, 
    productId: string
  ): Promise<DailyProduction | null> {
    try {
      const productionQuery = query(
        collection(db, 'daily_production'),
        where('date', '==', date),
        where('warehouseId', '==', warehouseId),
        where('productId', '==', productId)
      );
      
      const snapshot = await getDocs(productionQuery);
      
      if (snapshot.empty) {
        return null;
      }
      
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as DailyProduction;
      
    } catch (error) {
      console.error('Error getting daily production:', error);
      return null;
    }
  }

  /**
   * Update production quantity and sync with inventory
   */
  static async updateProductionQuantity(
    date: string,
    warehouseId: string,
    warehouseName: string,
    productId: string,
    productName: string,
    unit: string,
    newQuantity: number,
    createdBy: string
  ): Promise<{success: boolean, error?: string}> {
    
    try {
      // Get existing production record first
      const existingProduction = await this.getDailyProduction(date, warehouseId, productId);
      const oldQuantity = existingProduction?.productionQty || 0;
      const quantityDiff = newQuantity - oldQuantity;
      
      // Update daily_production collection
      const productionDocId = `${date}_${warehouseId}_${productId}`;
      const productionRef = doc(db, 'daily_production', productionDocId);
      
      const productionData: Partial<DailyProduction> = {
        date,
        warehouseId,
        warehouseName,
        productId,
        productName,
        unit,
        productionQty: newQuantity,
        updatedAt: serverTimestamp(),
        updatedBy: createdBy
      };
      
      if (!existingProduction) {
        productionData.createdAt = serverTimestamp();
        productionData.createdBy = createdBy;
      }
      
      // Update production record
      await setDoc(productionRef, productionData, { merge: true });
      
      // Update inventory if there's a quantity change
      if (quantityDiff !== 0) {
        // Use UnifiedInventoryTransactionService for inventory updates
        const inventoryResult = await UnifiedInventoryTransactionService.processInventoryTransaction({
          transactionType: 'production_inward',
          fromWarehouseId: 'NA', // External source
          toWarehouseId: warehouseId,
          productId,
          productName,
          unit,
          quantity: quantityDiff, // Only the difference
          reference: `PRODUCTION-${date}-${productionDocId}`,
          notes: `Production quantity update: ${oldQuantity} → ${newQuantity}`,
          createdBy
        });
        
        if (!inventoryResult.success) {
          throw new Error(`Inventory update failed: ${inventoryResult.error}`);
        }
      }
      
      return { success: true };
      
    } catch (error) {
      console.error('Error updating production quantity:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get all daily production data for a date and warehouses
   */
  static async getDailyProductionByDate(
    date: string, 
    warehouseIds: string[]
  ): Promise<DailyProduction[]> {
    try {
      const productionQuery = query(
        collection(db, 'daily_production'),
        where('date', '==', date),
        where('warehouseId', 'in', warehouseIds)
      );
      
      const snapshot = await getDocs(productionQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DailyProduction[];
      
    } catch (error) {
      console.error('Error getting daily production by date:', error);
      return [];
    }
  }

  /**
   * Calculate loaded out quantities from route_daily_status
   */
  static async getLoadedOutQuantities(
    date: string, 
    warehouseIds: string[]
  ): Promise<{[warehouseId: string]: {[productId: string]: number}}> {
    
    try {
      const loadedOutData: {[warehouseId: string]: {[productId: string]: number}} = {};
      
      // Initialize data structure
      warehouseIds.forEach(warehouseId => {
        loadedOutData[warehouseId] = {};
      });
      
      // Query route_daily_status for the date
      const routeStatusQuery = query(
        collection(db, 'route_daily_status'),
        where('date', '==', date)
      );
      
      const snapshot = await getDocs(routeStatusQuery);
      
      snapshot.docs.forEach(doc => {
        const routeStatus = doc.data();
        
        // Check if this route belongs to one of our warehouses
        const warehouseId = routeStatus.warehouseId;
        if (warehouseIds.includes(warehouseId) && routeStatus.detailedAllocations) {
          
          routeStatus.detailedAllocations.forEach((allocation: any) => {
            const productId = allocation.productId;
            const allocatedQty = allocation.openingQty || allocation.allocatedQty || 0;
            
            if (!loadedOutData[warehouseId][productId]) {
              loadedOutData[warehouseId][productId] = 0;
            }
            loadedOutData[warehouseId][productId] += allocatedQty;
          });
        }
      });
      
      return loadedOutData;
      
    } catch (error) {
      console.error('Error getting loaded out quantities:', error);
      return {};
    }
  }

  /**
   * Get current inventory for warehouses
   */
  static async getCurrentInventory(
    warehouseIds: string[]
  ): Promise<{[warehouseId: string]: {[productId: string]: {closingStock: number, unit: string}}}> {
    
    try {
      const inventoryData: {[warehouseId: string]: {[productId: string]: {closingStock: number, unit: string}}} = {};
      
      // Initialize data structure
      warehouseIds.forEach(warehouseId => {
        inventoryData[warehouseId] = {};
      });
      
      // Query inventory for main warehouses
      const inventoryQuery = query(
        collection(db, 'inventory'),
        where('warehouseType', '==', 'main'),
        where('warehouseId', 'in', warehouseIds)
      );
      
      const snapshot = await getDocs(inventoryQuery);
      
      snapshot.docs.forEach(doc => {
        const inventory = doc.data();
        const warehouseId = inventory.warehouseId;
        const productId = inventory.productId;
        
        inventoryData[warehouseId][productId] = {
          closingStock: inventory.closingStock || 0,
          unit: inventory.unit || 'KG'
        };
      });
      
      return inventoryData;
      
    } catch (error) {
      console.error('Error getting current inventory:', error);
      return {};
    }
  }

  /**
   * Build production summary for a single warehouse with all products
   */
  static async getWarehouseProductionSummary(
    warehouseId: string,
    warehouseName: string,
    date: string
  ): Promise<ProductionSummaryRow[]> {
    
    try {
      // Get all data in parallel
      const [
        productionData, 
        loadedOutData, 
        inventoryData, 
        productsSnapshot
      ] = await Promise.all([
        this.getDailyProductionByDate(date, [warehouseId]),
        this.getLoadedOutQuantities(date, [warehouseId]),
        this.getCurrentInventory([warehouseId]),
        getDocs(query(collection(db, 'products'), where('isActive', '==', true)))
      ]);
      
      // Build production data lookup
      const productionLookup: {[productId: string]: number} = {};
      productionData.forEach(prod => {
        productionLookup[prod.productId] = prod.productionQty;
      });
      
      // Get loaded out and inventory data for this warehouse
      const warehouseLoadedOut = loadedOutData[warehouseId] || {};
      const warehouseInventory = inventoryData[warehouseId] || {};
      
      // Build summary rows from ALL products
      const summaryRows: ProductionSummaryRow[] = [];
      
      productsSnapshot.docs.forEach(doc => {
        const product = doc.data();
        const productId = product.productId || doc.id;
        const productName = product.name || product.productName || 'Unknown Product';
        const unit = product.unit || 'KG';
        const category = product.category || product.categoryName || 'Unknown';
        
        const currentStock = warehouseInventory[productId]?.closingStock || 0;
        const loadedOut = warehouseLoadedOut[productId] || 0;
        const producedToday = productionLookup[productId] || 0;
        
        // Carried Forward = Current Inventory + Loaded Out Today - Produced Today
        const carriedForward = currentStock + loadedOut - producedToday;
        const freshProduction = producedToday;
        const openingStock = carriedForward + freshProduction;
        const liveStock = openingStock - loadedOut;
        
        summaryRows.push({
          productId,
          productName,
          unit,
          category,
          carriedForward,
          freshProduction,
          openingStock,
          loadedOut,
          liveStock,
          hasChanges: false
        });
      });
      
      return summaryRows.sort((a, b) => a.productName.localeCompare(b.productName));
      
    } catch (error) {
      console.error('Error building warehouse production summary:', error);
      return [];
    }
  }

  /**
   * Build production summary with all calculations
   * @deprecated Use getWarehouseProductionSummary instead for single warehouse view
   */
  static async buildProductionSummary(
    date: string,
    warehouseIds: string[],
    warehouseNames: {[id: string]: string}
  ): Promise<ProductionSummaryRow[]> {
    
    try {
      // Get all data in parallel
      const [
        productionData, 
        loadedOutData, 
        inventoryData, 
        productsSnapshot
      ] = await Promise.all([
        this.getDailyProductionByDate(date, warehouseIds),
        this.getLoadedOutQuantities(date, warehouseIds),
        this.getCurrentInventory(warehouseIds),
        getDocs(collection(db, 'products'))
      ]);
      
      // Build products lookup
      const productsLookup: {[id: string]: any} = {};
      productsSnapshot.docs.forEach(doc => {
        const product = doc.data();
        productsLookup[product.productId || doc.id] = {
          id: doc.id,
          name: product.name || product.productName,
          unit: product.unit || 'KG'
        };
      });
      
      // Build production data lookup
      const productionLookup: {[key: string]: number} = {};
      productionData.forEach(prod => {
        const key = `${prod.warehouseId}_${prod.productId}`;
        productionLookup[key] = prod.productionQty;
      });
      
      // Collect all unique product IDs
      const allProductIds = new Set<string>();
      
      // From inventory
      Object.values(inventoryData).forEach(warehouse => {
        Object.keys(warehouse).forEach(productId => allProductIds.add(productId));
      });
      
      // From production data
      productionData.forEach(prod => allProductIds.add(prod.productId));
      
      // From loaded out data
      Object.values(loadedOutData).forEach(warehouse => {
        Object.keys(warehouse).forEach(productId => allProductIds.add(productId));
      });
      
      // Build summary rows
      const summaryRows: ProductionSummaryRow[] = [];
      
      allProductIds.forEach(productId => {
        const product = productsLookup[productId];
        if (!product) return;
        
        const warehouseData: {[warehouseId: string]: ProductionWarehouseData} = {};
        
        warehouseIds.forEach(warehouseId => {
          const currentStock = inventoryData[warehouseId]?.[productId]?.closingStock || 0;
          const loadedOut = loadedOutData[warehouseId]?.[productId] || 0;
          const producedToday = productionLookup[`${warehouseId}_${productId}`] || 0;
          
          // Carried Forward = Current Inventory + Loaded Out Today - Produced Today
          const carriedForward = currentStock + loadedOut - producedToday;
          const freshProduction = producedToday;
          const openingStock = carriedForward + freshProduction;
          const liveStock = openingStock - loadedOut;
          
          warehouseData[warehouseId] = {
            warehouseId,
            warehouseName: warehouseNames[warehouseId] || warehouseId,
            carriedForward,
            freshProduction,
            openingStock,
            loadedOut,
            liveStock,
            hasChanges: false
          };
        });
        
        summaryRows.push({
          productId,
          productName: product.name,
          unit: product.unit,
          category: 'Legacy', // For old method compatibility
          carriedForward: 0,
          freshProduction: 0,
          openingStock: 0,
          loadedOut: 0,
          liveStock: 0,
          hasChanges: false
        });
      });
      
      return summaryRows.sort((a, b) => a.productName.localeCompare(b.productName));
      
    } catch (error) {
      console.error('Error building production summary:', error);
      return [];
    }
  }
}